import { Link } from 'react-router-dom';
import { PlusCircle } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { InlineCsatRating } from '../../components/ui/InlineCsatRating';
import { useTickets } from '../../hooks/use-tickets';
import { useAuthStore } from '../../stores/auth-store';
import { STATUS_COLORS, formatTicketId, getStatusLabel } from '../../lib/constants';
import { formatDate, timeAgo } from '../../lib/utils';

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useTickets({ pageSize: 5, sortBy: 'updatedAt', sortOrder: 'desc' });
  const tickets = data?.data ?? [];
  const { data: waitingData } = useTickets({ status: 'WAITING_CLIENT', pageSize: 10, sortBy: 'updatedAt', sortOrder: 'desc' });
  const waitingTickets = waitingData?.data ?? [];
  const waitingCount = waitingData?.meta.pagination?.total ?? waitingTickets.length;
  const { data: resolvedData } = useTickets({ status: 'RESOLVED', pageSize: 10, sortBy: 'updatedAt', sortOrder: 'desc' });
  const resolvedTickets = resolvedData?.data ?? [];

  return (
    <div className="space-y-6 lg:space-y-8">
      <section className="app-shell-panel rounded-[2rem] px-6 py-7 md:px-8 md:py-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-[62ch]">
            <p className="app-kicker">Client portal</p>
            <h1 className="mt-4 text-[clamp(2rem,4vw,3.15rem)] font-semibold leading-[0.95] text-[color:var(--ink-strong)]">Bine ai venit, {user?.name}.</h1>
            <p className="mt-4 max-w-[62ch] text-sm text-[color:var(--ink-muted)] md:text-base">
              Aici urmărești rapid tichetele active, cererile care au nevoie de clarificări și feedback-ul pe solicitările deja rezolvate.
            </p>
          </div>

          <Link to="/tickets/new" className="shrink-0">
            <Button size="lg" className="w-full justify-center lg:w-auto">
              <PlusCircle size={18} />
              Tichet Nou
            </Button>
          </Link>
        </div>
      </section>

      {/* Tickets needing clarification — shown before recent tickets */}
      {waitingTickets.length > 0 && (
        <Card>
          <div className="border-b border-[color:var(--surface-line)] px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="app-kicker">Necesită răspuns</p>
                <h2 className="mt-2 text-[1.15rem] font-bold tracking-[-0.02em] text-[color:var(--ink-strong)]">Tichete care necesită clarificări</h2>
              </div>
              <div className="rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] px-3.5 py-1.5 text-sm font-semibold text-[color:var(--ink-strong)]">
                {waitingCount} {waitingCount === 1 ? 'tichet' : 'tichete'}
              </div>
            </div>
          </div>
          <div className="divide-y">
            {waitingTickets.map((ticket) => (
              <Link
                key={ticket.id}
                to={`/tickets/${ticket.id}`}
                className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-[var(--surface-panel-strong)]/80"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-[color:var(--ink-strong)]">{ticket.subject}</p>
                  <p className="text-xs text-[color:var(--ink-muted)]">{timeAgo(ticket.updatedAt)}</p>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">La mine</Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* Resolved tickets needing CSAT feedback */}
      {resolvedTickets.length > 0 && (
        <Card>
          <div className="border-b border-[color:var(--surface-line)] px-6 py-5">
            <p className="app-kicker">Feedback</p>
            <h2 className="mt-2 text-xl font-semibold text-[color:var(--ink-strong)]">Tichete rezolvate — acordă un scor</h2>
            <p className="mt-2 text-xs text-[color:var(--ink-muted)]">Apasă pe stele pentru a oferi feedback și a închide tichetul.</p>
          </div>
          <div className="divide-y">
            {resolvedTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-[var(--surface-panel-strong)]/80"
              >
                <Link to={`/tickets/${ticket.id}`} className="min-w-0 flex-1">
                  <p className="truncate font-medium text-[color:var(--ink-strong)]">{ticket.subject}</p>
                  <p className="text-xs text-[color:var(--ink-muted)]">{timeAgo(ticket.updatedAt)}</p>
                </Link>
                <InlineCsatRating ticketId={ticket.id} />
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="border-b border-[color:var(--surface-line)] px-6 py-5">
          <p className="app-kicker">Activitate recentă</p>
          <h2 className="mt-2 text-[1.15rem] font-bold tracking-[-0.02em] text-[color:var(--ink-strong)]">Tichete recente</h2>
        </div>
        <div className="divide-y">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          {!isLoading && tickets.length === 0 && (
            <p className="px-6 py-8 text-center text-sm text-[color:var(--ink-muted)]">Nu ai tichete încă</p>
          )}
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              className="grid gap-3 px-6 py-4 transition-colors hover:bg-[var(--surface-panel-strong)]/80 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
            >
              <Link
                to={`/tickets/${ticket.id}`}
                className="min-w-0 flex-1"
                aria-label={`Deschide tichetul ${formatTicketId(ticket.numericId)}: ${ticket.subject}`}
              >
                <p className="font-medium tracking-[-0.02em] text-[color:var(--ink-strong)]">#{formatTicketId(ticket.numericId)}</p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[color:var(--ink-muted)]">
                  <span>Creat pe {formatDate(ticket.createdAt)}</span>
                  <span>Actualizat {timeAgo(ticket.updatedAt)}</span>
                </div>
              </Link>
              <div className="flex items-center gap-2 md:justify-end">
                {ticket.status === 'RESOLVED' && <InlineCsatRating ticketId={ticket.id} />}
                <Badge className={STATUS_COLORS[ticket.status]}>
                  {getStatusLabel(ticket.status, 'CLIENT')}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
